/**
 * Regeln & Produkte (spec §1.4, §1.12).
 *
 * Zwei Bereiche auf einer Seite, weil sie zusammen gelesen werden: die Regeln sagen, wer für ein
 * Produkt was bekommt, und die Suchbegriffe sagen, woran ein Produkt in einem Invoice-Namen erkannt
 * wird. Ein Produkt, auf das keine gültige Regel zeigt, steht oben als Prüfzeile `produkt_ohne_regel`
 * mit einem Verweis, der genau dieses Produkt im Editor vorbelegt.
 *
 * Die Sperre ist der Kern dieses Bildschirms: „Regel speichern“ ist deaktiviert, solange der
 * Fingerabdruck der aktuellen Formularwerte nicht dem des zuletzt geprüften Trockenlaufs entspricht.
 * Wer ein Feld ändert, muss erneut prüfen. Der Trockenlauf selbst ist ein Serveraufruf; die drei
 * Summen darunter sind die Summen, die der Server zurückgegeben hat — hier wird nichts addiert und
 * nichts über Provisionen entschieden.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  dryRunRuleSchema, matchTestSchema, replaceTermsSchema, upsertProductSchema, upsertRuleSchema,
} from '../../../../shared/schemas/catalogue'
import {
  api, type BegriffZeile, type MatchTestErgebnis, type ProbeErgebnis, type ProbeZeile,
  type ProduktMitBegriffen, type ProduktZeile, type PruefenEintrag, type RegelErgebnis,
  type RegelZeile, type TeamZeile,
} from '../../../api'
import { Betrag } from '../../../components/Betrag'
import { Feld } from '../../../components/Feld'
import { Knopf } from '../../../components/Knopf'
import { useInsBild } from '../../../components/insBild'
import { Leiste } from '../../../components/Leiste'
import { Tabelle } from '../../../components/Tabelle'
import { Verweis } from '../../../components/Verweis'
import { TabellenZustand, fehlermeldung } from '../../../components/Zustand'
import { datum, prozent } from '../../../format'
import {
  ANWENDUNG_TEXT, BEDINGUNGS_FELDER, BETRAGS_MODUS_TEXT, EMPFAENGER_ROLLE_TEXT, LEAD_QUELLE_TEXT, ROLLENWAHL_TEXT, VORLAGEN,
  abdruck, ableiten, formularAusRegel, koerperAusFormular, leeresFormular, rollenwahl, schluesselAus, waehleRolle,
  type BedingungFormular, type BedingungsOperator, type BetragsModus, type LeadQuelle, type RegelFormular, type Rollenwahl,
} from '../../../regelnModell'
import { STANDARD_SCHLUESSEL } from '../../../../shared/standardregeln'
import type { SeitenProps } from '../../../screen'
import { berlinDate } from '../../../../shared/dates'

/** Der bearbeitete Datensatz steht in der Adresse, damit der Verweis aus der Prüfliste ihn setzen kann. */
export interface RegelnSuche { regel?: string; produkt?: string }

/** So viele <th> stehen im Kopf der jeweiligen Tabelle — die Zustandszeilen spannen genau darüber. */
const SPALTEN_REGELN = 7
const SPALTEN_PRODUKTE = 5

const MODUS_TEXT: Readonly<Record<RegelErgebnis['modus'], string>> = {
  insert: 'Die Regel wurde neu angelegt.',
  update_in_place: 'Die Regel wurde direkt geändert — auf sie verweist noch keine Provision.',
  versioned: 'Die bisherige Regel wurde beendet und eine neue Version mit heutigem Beginn angelegt.',
}

/** Die Antwort beider Produkt-Routen (routes/tenant.ts): die geschriebene Zeile und die Nachrechnung. */
interface ProduktAntwort { produkt: ProduktZeile; neu_berechnet: boolean }

interface ZodMeldung { path: readonly PropertyKey[]; message: string }

/** Die erste deutsche zod-Meldung. Dieselbe Fehlerkarte wie im Worker (schemas/de.ts). */
function ersteMeldung(fehler: { issues: readonly ZodMeldung[] }): string {
  return fehler.issues[0]?.message ?? 'Die Eingabe ist ungültig.'
}

/**
 * Der heutige Geschäftstag — Berliner Zeit, nicht UTC.
 *
 * `toISOString()` liefert den UTC-Tag. Von Ende März bis Ende Oktober liegt Berlin zwei Stunden
 * davor: wer am 1. Juni um 01:30 Uhr eine Regel anlegt, bekäme als `valid_from` den 31. Mai — und
 * damit einen Gültigkeitsbeginn, der einen Tag in der Vergangenheit liegt und die Neuberechnung
 * über bereits abgerechnete Zahlungen zieht. Der Server datiert alles nach Berlin (shared/dates.ts);
 * die Oberfläche schlägt deshalb denselben Tag vor.
 */
/** Das Kürzel aus dem ersten Wort des Namens: Großbuchstaben und Ziffern, Umlaute übersetzt. */
function kuerzelAus(name: string): string {
  const erstes = name.trim().split(/\s+/)[0] ?? ''
  return schluesselAus(erstes).replace(/_/g, '')
}

function heuteIso(): string {
  return berlinDate(new Date())
}

/**
 * Der Server rechnet nach jedem Schreiben in diesem Katalog nach und meldet mit `neu_berechnet`, ob er
 * die Sperre des Auftraggebers bekommen hat. Bekam er sie nicht, ist geschrieben, aber nicht gerechnet
 * — das darf nicht still bleiben, gleichgültig ob es eine Regel, ein Produkt oder eine Begriffsliste
 * war. Der zweite Anlauf geht durch dieselbe Route wie das Schreiben; einen eigenen Endpunkt für die
 * Neuberechnung gibt es nicht, und jeder dieser Aufrufe ist wiederholbar.
 */
function NeuberechnungOffen({ was, laeuft, onWiederholen }: {
  was: string
  laeuft: boolean
  onWiederholen: () => void
}): ReactNode {
  return (
    <>
      <p role="alert" className="fehler">
        Die Neuberechnung lief nicht — der Auftraggeber war in diesem Moment belegt. {was}, die offenen
        Provisionen sind noch nicht angepasst.
      </p>
      <p className="aktionen">
        <Knopf disabled={laeuft} onClick={onWiederholen}>Neuberechnung erneut anstoßen</Knopf>
      </p>
    </>
  )
}

function empfaengerName(tcId: string | null, team: readonly TeamZeile[]): string {
  if (tcId === null) return 'ohne Empfänger'
  return team.find((zeile) => zeile.tenant_contractor_id === tcId)?.display_name ?? tcId
}

/** „Wer bekommt“ in einem Satzteil: entweder eine Rolle des Abschlusses oder eine feste Person. */
function empfaengerText(formular: RegelFormular, team: readonly TeamZeile[]): string {
  if (formular.recipient_mode === 'fixed') return empfaengerName(formular.recipient_tc_id, team)
  return formular.recipient_deal_role === null
    ? 'Rolle des Abschlusses'
    : EMPFAENGER_ROLLE_TEXT[formular.recipient_deal_role]
}

export const Route = createFileRoute('/t/$tenantId/regeln')({
  validateSearch: (roh: Record<string, unknown>): RegelnSuche => ({
    regel: typeof roh.regel === 'string' ? roh.regel : undefined,
    produkt: typeof roh.produkt === 'string' ? roh.produkt : undefined,
  }),
  component: RegelnRoute,
})

function RegelnRoute(): ReactNode {
  const { tenantId } = Route.useParams()
  const suche = Route.useSearch()
  const navigate = useNavigate()
  return (
    <RegelnSeite
      tenantId={tenantId}
      suche={suche}
      setzeSuche={(naechste) => { void navigate({ to: '.', search: { ...suche, ...naechste } }) }}
      gehZu={(pfad) => { void navigate({ to: pfad }) }}
    />
  )
}

export function RegelnSeite({ tenantId, suche, setzeSuche, gehZu }: SeitenProps<RegelnSuche>): ReactNode {
  // Ein Pfadabschnitt aus einem Route-Parameter geht nirgends ungeprüft in eine Adresse.
  const tid = encodeURIComponent(tenantId)

  const regeln = useQuery({
    queryKey: ['regeln', tenantId],
    queryFn: () => api.get<{ regeln: RegelZeile[] }>(`/api/t/${tid}/regeln`),
  })
  const produkte = useQuery({
    queryKey: ['produkte', tenantId],
    queryFn: () => api.get<{ produkte: ProduktMitBegriffen[] }>(`/api/t/${tid}/produkte`),
  })
  // Eine Regel darf auf eine feste Person zeigen; deren Name steht am Team-Eintrag, nicht an der Regel.
  const team = useQuery({
    queryKey: ['team', tenantId],
    queryFn: () => api.get<{ team: TeamZeile[] }>(`/api/t/${tid}/team`),
  })
  const ohneRegel = useQuery({
    queryKey: ['inbox', tenantId, 'produkt_ohne_regel'],
    queryFn: () => api.get<{ items: PruefenEintrag[] }>(
      `/api/t/${tid}/inbox`, { kind: 'produkt_ohne_regel' }),
  })

  const queryClient = useQueryClient()
  const standard = useMutation({
    mutationFn: () => api.post<{ angelegt: string[]; vorhanden: string[] }>(`/api/t/${tid}/regeln/standard`, {}),
    onSuccess: () => {
      for (const schl of ['regeln', 'inbox', 'provisionen', 'dashboard']) {
        void queryClient.invalidateQueries({ queryKey: [schl, tenantId] })
      }
    },
  })

  const alleRegeln = regeln.data?.regeln ?? []
  const alleProdukte = produkte.data?.produkte ?? []
  const alleTeam = team.data?.team ?? []
  const hinweise = ohneRegel.data?.items ?? []

  const gewaehlteRegel = useMemo(
    () => alleRegeln.find((regel) => regel.id === suche.regel) ?? null,
    [alleRegeln, suche.regel],
  )

  // Der Schlüssel setzt den Editor zurück, sobald ein anderer Datensatz gewählt ist: ein halb
  // getippter Wert der vorigen Regel dürfte nie in den Rumpf der nächsten geraten.
  const regelEditor = (
    <RegelEditor
      key={gewaehlteRegel?.id ?? suche.produkt ?? 'neu'}
      tenantId={tenantId}
      regel={gewaehlteRegel}
      produkte={alleProdukte}
      team={alleTeam}
      fehlerProdukte={produkte.isError ? produkte.error : null}
      fehlerTeam={team.isError ? team.error : null}
      vorgabeProduktId={suche.produkt}
    />
  )
  useInsBild(gewaehlteRegel === null ? null : 'regel-editor')

  return (
    <div className="seite">
      <h1>Produkte &amp; Regeln</h1>

      {hinweise.length === 0 ? null : (
        <section className="detail" aria-label="Produkte ohne Regel">
          <h2>Produkte ohne Regel</h2>
          {hinweise.map((eintrag) => (
            <p key={eintrag.subject_id} className="fehler">
              Für dieses Produkt gibt es keine aktive Regel: {eintrag.note ?? eintrag.subject_id}.{' '}
              <Verweis
                href={`/t/${tid}/regeln?produkt=${encodeURIComponent(eintrag.subject_id)}`}
                gehZu={gehZu}
              >
                Regel anlegen
              </Verweis>
            </p>
          ))}
        </section>
      )}

      {/* Produkte zuerst: ohne Produkt erkennt keine Zahlung ihren Abschluss, und eine Regel ohne
          Produkt läuft ins Leere. Wer neu anfängt, legt also erst an, was er verkauft. */}
      <ProduktBereich
        tenantId={tenantId}
        produkte={alleProdukte}
        laedt={produkte.isPending}
        fehler={produkte.isError ? produkte.error : null}
        onNeuLaden={() => { void produkte.refetch() }}
      />

      <h2>Regeln</h2>
      <Tabelle
        beschriftung="Provisionsregeln dieses Auftraggebers"
        kopf={
          <tr>
            <th scope="col">Regel</th>
            <th scope="col">Produkte</th>
            <th scope="col">Wer bekommt</th>
            <th scope="col">Was</th>
            <th scope="col">Wann</th>
            <th scope="col">Gültig</th>
            <th scope="col"><span className="nur-vorlesen">Bearbeiten</span></th>
          </tr>
        }
      >
        {regeln.isPending ? (
          <TabellenZustand art="laedt" spalten={SPALTEN_REGELN} beschriftung="Die Regeln werden geladen." />
        ) : null}
        {regeln.isError ? (
          <TabellenZustand
            art="fehler"
            spalten={SPALTEN_REGELN}
            fehler={regeln.error}
            onWiederholen={() => { void regeln.refetch() }}
          />
        ) : null}
        {!regeln.isPending && !regeln.isError && alleRegeln.length === 0 ? (
          <TabellenZustand
            art="leer"
            spalten={SPALTEN_REGELN}
            titel="Noch keine Regel angelegt"
            hinweis="Ohne Regel entsteht zu keiner Zahlung eine Provision. Legen Sie je Produkt fest, wer welchen Anteil bekommt."
          />
        ) : null}
        {alleRegeln.map((regel) => {
          const formular = formularAusRegel(regel)
          return (
            <Fragment key={regel.id}>
            <tr aria-selected={regel.id === gewaehlteRegel?.id ? true : undefined}>
              <th scope="row" title={regel.key}>{regel.name}</th>
              <td>{formular.alle_produkte ? 'alle Produkte' : formular.product_codes.join(', ')}</td>
              <td>{empfaengerText(formular, alleTeam)}</td>
              <td>
                {BETRAGS_MODUS_TEXT[formular.amount_mode]}
                {formular.amount_mode === 'fixed' && regel.amount_cents !== null ? (
                  <> — <Betrag cents={regel.amount_cents} /></>
                ) : null}
                {formular.amount_mode === 'pct_of_net' && regel.rate_bp !== null
                  ? ` — ${prozent(regel.rate_bp)}`
                  : null}
              </td>
              <td>{ANWENDUNG_TEXT[formular.applies_to]}</td>
              <td className="mono">
                {datum(regel.valid_from)}
                {regel.valid_to === null ? ' – offen' : ` – ${datum(regel.valid_to)}`}
              </td>
              <td>
                <Knopf variante="still" onClick={() => { setzeSuche({ regel: regel.id }) }}>
                  Bearbeiten
                </Knopf>
              </td>
            </tr>
            {regel.id === gewaehlteRegel?.id ? (
              <tr className="zeile-detail">
                <td colSpan={SPALTEN_REGELN}>
                  <div className="zeile-detail-inhalt" id="regel-editor">{regelEditor}</div>
                </td>
              </tr>
            ) : null}
            </Fragment>
          )
        })}
      </Tabelle>

      <p className="aktionen">
        <Knopf variante="still" onClick={() => { setzeSuche({ regel: undefined }) }}>Neue Regel</Knopf>
        {/* Nur solange keine der Standardregeln da ist: ein Auftraggeber, der vor diesem Regelwerk
            angelegt wurde, holt sie sich mit einem Klick; einer, der sie hat oder bewusst beendet
            hat, sieht den Knopf nicht mehr. */}
        {regeln.isSuccess && !alleRegeln.some((r) => STANDARD_SCHLUESSEL.includes(r.key)) ? (
          <Knopf disabled={standard.isPending} onClick={() => { standard.mutate() }}>Standardregeln anlegen</Knopf>
        ) : null}
      </p>
      {standard.isSuccess ? (
        <p className="meldung">
          {standard.data.angelegt.length === 0
            ? 'Die Standardregeln waren schon da.'
            : `${standard.data.angelegt.length} Standardregeln angelegt: Closer 10 %, Setter 5 %, Empfehlungsgeber 2,5 % — jede Rate; bei gestelltem Lead die Hälfte für Closer und Setter.`}
        </p>
      ) : null}
      {standard.isError ? <p role="alert" className="fehler">{fehlermeldung(standard.error)}</p> : null}

      {/* Eine bestehende Regel bearbeitet man in ihrer Tabellenzeile; hier unten steht nur „Neue Regel". */}
      {gewaehlteRegel === null ? regelEditor : null}

    </div>
  )
}

/** Der zuletzt geprüfte Trockenlauf samt Fingerabdruck der Werte, für die er gilt. */
interface Geprueft { abdruck: string; ergebnis: ProbeErgebnis }

function RegelEditor({ tenantId, regel, produkte, team, fehlerProdukte, fehlerTeam, vorgabeProduktId }: {
  tenantId: string
  regel: RegelZeile | null
  produkte: readonly ProduktMitBegriffen[]
  team: readonly TeamZeile[]
  /** Kam die Liste nicht an, steht das an der Auswahl: eine leere Liste hieße sonst „gibt es nicht“. */
  fehlerProdukte: unknown
  fehlerTeam: unknown
  vorgabeProduktId?: string
}): ReactNode {
  const tid = encodeURIComponent(tenantId)
  const queryClient = useQueryClient()
  const vorgabeCode = produkte.find((produkt) => produkt.id === vorgabeProduktId)?.code
  const neu = regel === null
  const [formular, setzeFormular] = useState<RegelFormular>(() => {
    if (regel !== null) return formularAusRegel(regel)
    const leer = { ...leeresFormular(heuteIso()), ...waehleRolle('closer') }
    return vorgabeCode === undefined ? leer : { ...leer, product_codes: [vorgabeCode] }
  })
  /**
   * Was das Formular zuletzt als Name und Schlüssel vorgeschlagen hat. Steht im Feld noch genau der
   * Vorschlag, folgt es der nächsten Änderung; hat der Benutzer etwas anderes hineingeschrieben,
   * bleibt seins. Nur bei einer neuen Regel — eine gespeicherte behält Name und Schlüssel.
   */
  const [vorschlag, setzeVorschlag] = useState<{ name: string; key: string }>(() => neu ? ableiten(formular) : { name: '', key: '' })
  const [vorlage, setzeVorlage] = useState('')
  const [geprueft, setzeGeprueft] = useState<Geprueft | null>(null)
  const [pruefFehler, setzePruefFehler] = useState<string | null>(null)
  const [gespeichert, setzeGespeichert] = useState<RegelErgebnis | null>(null)
  /** Der Trockenlauf hat Rückbuchungen gefunden; gespeichert wird erst nach einem zweiten, wissenden Klick. */
  const [rueckfrage, setzeRueckfrage] = useState(false)

  // Der Fingerabdruck sagt, ob ein Trockenlauf zu genau diesen Werten gehört — er entscheidet, ob
  // die Anzeige unten noch stimmt, nicht mehr, ob gespeichert werden darf.
  const koerper = useMemo(() => koerperAusFormular(formular), [formular])
  const aktuellerAbdruck = useMemo(() => abdruck(koerper), [koerper])
  const freigegeben = geprueft !== null && geprueft.abdruck === aktuellerAbdruck

  /**
   * Der Fingerabdruck reist mit dem Auftrag, nicht im Abschluss der Rückmeldung: react-query ruft das
   * `onSuccess` des *letzten* Renderns auf, und wer während des laufenden Trockenlaufs weitertippt,
   * bekäme sonst das Ergebnis der alten Werte für die neuen freigegeben — genau die Freigabe, die
   * dieser Bildschirm verhindern soll.
   */
  const trockenlauf = useMutation({
    mutationFn: (auftrag: { rumpf: unknown; abdruck: string }) =>
      api.post<ProbeErgebnis>(`/api/t/${tid}/regeln/dry-run`, auftrag.rumpf),
    onSuccess: (ergebnis, auftrag) => { setzeGeprueft({ abdruck: auftrag.abdruck, ergebnis }) },
  })
  const speichern = useMutation({
    mutationFn: (rumpf: unknown) => regel === null
      ? api.post<RegelErgebnis>(`/api/t/${tid}/regeln`, rumpf)
      : api.patch<RegelErgebnis>(`/api/t/${tid}/regeln/${encodeURIComponent(regel.id)}`, rumpf),
    onSuccess: (antwort) => {
      setzeGespeichert(antwort)
      setzeRueckfrage(false)
      for (const schl of ['regeln', 'inbox', 'provisionen', 'dashboard']) {
        void queryClient.invalidateQueries({ queryKey: [schl, tenantId] })
      }
    },
  })
  /**
   * Der zweite Anlauf der Neuberechnung. Er geht durch dieselbe Route wie das Speichern: der Server
   * rechnet nach jedem Schreiben nach und meldet mit `neu_berechnet`, ob er die Sperre des
   * Auftraggebers bekommen hat. Ein eigener Endpunkt dafür existiert nicht.
   */
  const nachberechnen = useMutation({
    mutationFn: (ziel: { id: string; rumpf: unknown }) =>
      api.patch<RegelErgebnis>(`/api/t/${tid}/regeln/${encodeURIComponent(ziel.id)}`, ziel.rumpf),
    onSuccess: (antwort) => {
      setzeGespeichert(antwort)
      for (const schl of ['regeln', 'provisionen', 'dashboard']) {
        void queryClient.invalidateQueries({ queryKey: [schl, tenantId] })
      }
    },
  })

  /** Jede Änderung aus den einfachen Feldern; Name und Schlüssel folgen, solange sie noch Vorschlag sind. */
  const aendere = (teil: Partial<RegelFormular>): void => {
    const naechstes = { ...formular, ...teil }
    if (!neu) {
      setzeFormular(naechstes)
      return
    }
    const abgeleitet = ableiten(naechstes)
    const nameFolgt = naechstes.name.trim() === '' || naechstes.name === vorschlag.name
    const keyFolgt = naechstes.key.trim() === '' || naechstes.key === vorschlag.key
    setzeFormular({
      ...naechstes,
      name: nameFolgt ? abgeleitet.name : naechstes.name,
      key: keyFolgt ? abgeleitet.key : naechstes.key,
    })
    setzeVorschlag(abgeleitet)
  }
  /** Die Felder unter „Erweitert“ ändern sich nur von Hand — hier folgt nichts nach. */
  const aendereErweitert = (teil: Partial<RegelFormular>): void => { setzeFormular({ ...formular, ...teil }) }

  const uebernehmeVorlage = (id: string): void => {
    setzeVorlage(id)
    const gewaehlt = VORLAGEN.find((v) => v.id === id)
    if (gewaehlt === undefined) return
    const basis = { ...leeresFormular(heuteIso()), ...gewaehlt.werte, valid_from: formular.valid_from }
    const abgeleitet = ableiten(basis)
    setzeFormular({ ...basis, name: abgeleitet.name, key: abgeleitet.key })
    setzeVorschlag(abgeleitet)
    setzeGeprueft(null)
  }

  const pruefen = (): void => {
    const geprueftesFormular = dryRunRuleSchema.safeParse(koerper)
    if (!geprueftesFormular.success) {
      setzePruefFehler(ersteMeldung(geprueftesFormular.error))
      return
    }
    setzePruefFehler(null)
    trockenlauf.mutate({ rumpf: geprueftesFormular.data, abdruck: aktuellerAbdruck })
  }

  /**
   * Ein Klick: prüfen, dann schreiben. Der Trockenlauf bleibt — er läuft nur nicht mehr als eigener
   * Handgriff, den man vergessen oder als Hürde empfinden kann. Findet er Rückbuchungen, also Geld,
   * das schon ausgezahlt ist und zurückgeholt würde, hält der Vorgang an und fragt; alles andere
   * wird gespeichert, und was sich geändert hat, steht danach unter dem Formular.
   */
  const absenden = (wissend = false): void => {
    const geprueftesFormular = upsertRuleSchema.safeParse(koerper)
    if (!geprueftesFormular.success) {
      setzePruefFehler(ersteMeldung(geprueftesFormular.error))
      return
    }
    const probe = dryRunRuleSchema.safeParse(koerper)
    if (!probe.success) {
      setzePruefFehler(ersteMeldung(probe.error))
      return
    }
    setzePruefFehler(null)
    setzeRueckfrage(false)
    const abdruckJetzt = aktuellerAbdruck
    const rumpf = geprueftesFormular.data
    void trockenlauf.mutateAsync({ rumpf: probe.data, abdruck: abdruckJetzt }).then((ergebnis) => {
      if (ergebnis.rueckbuchungen.length > 0 && !wissend) {
        setzeRueckfrage(true)
        return
      }
      speichern.mutate(rumpf)
    }).catch(() => undefined)
  }

  const fehler = pruefFehler
    ?? (trockenlauf.isError ? fehlermeldung(trockenlauf.error) : null)
    ?? (speichern.isError ? fehlermeldung(speichern.error) : null)
    ?? (nachberechnen.isError ? fehlermeldung(nachberechnen.error) : null)

  const wahl = rollenwahl(formular)

  return (
    <section className="detail" aria-label="Regel bearbeiten">
      <h2>{regel === null ? 'Neue Regel' : `Regel: ${regel.name}`}</h2>

      {neu ? (
        <Leiste beschriftung="Vorlagen">
          <Feld label="Vorlage" htmlFor="rl-vorlage" hinweis="Füllt das Formular vor. Alles darunter bleibt änderbar.">
            <select id="rl-vorlage" value={vorlage} onChange={(e) => { uebernehmeVorlage(e.target.value) }}>
              <option value="">— selbst zusammenstellen —</option>
              {VORLAGEN.map((v) => <option key={v.id} value={v.id}>{v.text}</option>)}
            </select>
          </Feld>
        </Leiste>
      ) : null}

      <fieldset>
        <legend>Wer bekommt</legend>
        {fehlerTeam === null ? null : (
          <p className="fehler">
            Das Team konnte nicht geladen werden, die Personenliste ist deshalb leer.{' '}
            {fehlermeldung(fehlerTeam)}
          </p>
        )}
        <Feld label="Rolle" htmlFor="rl-rolle" hinweis="Closer, Setter und Empfehlungsgeber stehen am Abschluss. Eine feste Person bekommt unabhängig davon — etwa eine Teamleitung oder eine Agentur.">
          <select
            id="rl-rolle"
            value={wahl}
            onChange={(e) => { aendere(waehleRolle(e.target.value as Rollenwahl)) }}
          >
            {(Object.keys(ROLLENWAHL_TEXT) as Rollenwahl[]).map((r) => (
              <option key={r} value={r}>{ROLLENWAHL_TEXT[r]}</option>
            ))}
          </select>
        </Feld>
        {wahl === 'sonstige' ? (
          <Feld label="Bezeichnung der Rolle" htmlFor="rl-rolelabel" hinweis="So heißt die Rolle am Abschluss und auf der Provisionszeile — etwa „Host“.">
            <input id="rl-rolelabel" type="text" value={formular.role_label === 'Sonstige' ? '' : formular.role_label} placeholder="Host"
              onChange={(e) => { aendere({ role_label: e.target.value === '' ? 'Sonstige' : e.target.value, role_code: e.target.value === '' ? 'SONSTIGE' : schluesselAus(e.target.value) }) }} />
          </Feld>
        ) : null}
        {wahl === 'fixed' ? (
          <>
            <Feld label="Person" htmlFor="rl-person">
              <select
                id="rl-person"
                value={formular.recipient_tc_id ?? ''}
                onChange={(e) => { aendere({ recipient_tc_id: e.target.value === '' ? null : e.target.value }) }}
              >
                <option value="">bitte wählen</option>
                {team.map((zeile) => (
                  <option key={zeile.tenant_contractor_id} value={zeile.tenant_contractor_id}>
                    {zeile.display_name}
                  </option>
                ))}
              </select>
            </Feld>
            <Feld label="Wofür" htmlFor="rl-rolelabel" hinweis="Steht als Rolle auf der Provisionszeile, z. B. „Overhead“ oder „Agentur“.">
              <input id="rl-rolelabel" type="text" value={formular.role_label}
                onChange={(e) => { aendere({ role_label: e.target.value, role_code: schluesselAus(e.target.value) }) }} />
            </Feld>
          </>
        ) : null}
      </fieldset>

      <fieldset>
        <legend>Wie viel und wann</legend>
        <Feld label="Berechnung" htmlFor="rl-berechnung">
          <select
            id="rl-berechnung"
            value={formular.amount_mode}
            onChange={(e) => { aendere({ amount_mode: e.target.value as BetragsModus }) }}
          >
            {Object.entries(BETRAGS_MODUS_TEXT).map(([wert, text]) => (
              <option key={wert} value={wert}>{text}</option>
            ))}
          </select>
        </Feld>
        {formular.amount_mode === 'fixed' ? (
          <Feld label="Betrag in Euro" htmlFor="rl-betrag">
            <input id="rl-betrag" type="text" inputMode="decimal" value={formular.betrag_eingabe}
              onChange={(e) => { aendere({ betrag_eingabe: e.target.value }) }} />
          </Feld>
        ) : null}
        {formular.amount_mode === 'pct_of_net' ? (
          <Feld label="Prozent vom Netto" htmlFor="rl-prozent">
            <input id="rl-prozent" type="text" inputMode="decimal" value={formular.prozent_eingabe}
              onChange={(e) => { aendere({ prozent_eingabe: e.target.value }) }} />
          </Feld>
        ) : null}
        <Feld label="Anwendung" htmlFor="rl-anwendung" hinweis="„Nur 1. Rate“ heißt einmalig: der Anteil an der ersten Zahlung, danach nichts mehr.">
          <select
            id="rl-anwendung"
            value={formular.applies_to}
            onChange={(e) => { aendere({ applies_to: e.target.value === 'first_rate' ? 'first_rate' : 'every_rate' }) }}
          >
            <option value="every_rate">jede Rate</option>
            <option value="first_rate">nur 1. Rate (einmalig)</option>
          </select>
        </Feld>
        <Feld label="Lead-Quelle" htmlFor="rl-lead" hinweis="Ein Abschluss ohne Vermerk zählt als intern. Extern wird er per FinSuites-Tag (Einstellungen → Webhook).">
          <select
            id="rl-lead"
            value={formular.lead_quelle}
            onChange={(e) => { aendere({ lead_quelle: e.target.value as LeadQuelle }) }}
          >
            {(Object.keys(LEAD_QUELLE_TEXT) as LeadQuelle[]).map((q) => (
              <option key={q} value={q}>{LEAD_QUELLE_TEXT[q]}</option>
            ))}
          </select>
        </Feld>
      </fieldset>

      <fieldset>
        <legend>Produkte</legend>
        {fehlerProdukte === null ? null : (
          <p className="fehler">
            Die Produkte konnten nicht geladen werden, die Auswahl ist deshalb leer.{' '}
            {fehlermeldung(fehlerProdukte)}
          </p>
        )}
        <p>
          <label>
            <input
              type="checkbox"
              checked={formular.alle_produkte}
              onChange={(e) => { aendere({ alle_produkte: e.target.checked }) }}
            />
            {' '}Alle Produkte
          </label>
        </p>
        {formular.alle_produkte ? null : produkte.map((produkt) => (
          <p key={produkt.id}>
            <label>
              <input
                type="checkbox"
                checked={formular.product_codes.includes(produkt.code)}
                onChange={(e) => {
                  aendere({
                    product_codes: e.target.checked
                      ? [...formular.product_codes, produkt.code]
                      : formular.product_codes.filter((code) => code !== produkt.code),
                  })
                }}
              />
              {' '}{produkt.code} — {produkt.name}
            </label>
          </p>
        ))}
      </fieldset>

      <Leiste beschriftung="Gültigkeit">
        <Feld label="Gültig ab" htmlFor="rl-von">
          <input id="rl-von" type="date" value={formular.valid_from}
            onChange={(e) => { aendere({ valid_from: e.target.value }) }} />
        </Feld>
        <Feld label="Gültig bis" htmlFor="rl-bis" info="Leer: unbefristet. Eine Regel wird nie gelöscht, nur beendet.">
          <input id="rl-bis" type="date" value={formular.valid_to}
            onChange={(e) => { aendere({ valid_to: e.target.value }) }} />
        </Feld>
      </Leiste>

      {/* Alles, was die Maschine braucht und der Betreiber nicht: Schlüssel, Codes, Leistungstext,
          weitere Bedingungen. Vorbelegt aus den Feldern oben; wer hier etwas ändert, behält es. */}
      <details className="detail">
        <summary>Erweitert</summary>
        <Leiste beschriftung="Bezeichnung">
          <Feld label="Name der Regel" htmlFor="rl-name">
            <input id="rl-name" type="text" value={formular.name}
              onChange={(e) => { aendereErweitert({ name: e.target.value }) }} />
          </Feld>
          <Feld label="Schlüssel" htmlFor="rl-key" info="Eindeutig je Auftraggeber; Großbuchstaben, Ziffern, Unterstrich.">
            <input id="rl-key" type="text" value={formular.key}
              onChange={(e) => { aendereErweitert({ key: e.target.value }) }} />
          </Feld>
          <Feld label="Rollen-Code" htmlFor="rl-rolecode">
            <input id="rl-rolecode" type="text" value={formular.role_code}
              onChange={(e) => { aendereErweitert({ role_code: e.target.value }) }} />
          </Feld>
          {wahl === 'fixed' || wahl === 'sonstige' ? null : (
            <Feld label="Rollenbezeichnung" htmlFor="rl-rolelabel">
              <input id="rl-rolelabel" type="text" value={formular.role_label}
                onChange={(e) => { aendereErweitert({ role_label: e.target.value }) }} />
            </Feld>
          )}
          <Feld
            label="Leistungstext"
            htmlFor="rl-leistung"
            hinweis="Dieser Satz steht später als Position auf der Rechnung des Auftragnehmers."
          >
            <input id="rl-leistung" type="text" value={formular.leistung_text}
              onChange={(e) => { aendereErweitert({ leistung_text: e.target.value }) }} />
          </Feld>
          <Feld
            label="Ohne Empfänger"
            htmlFor="rl-unassigned"
            hinweis="Steht die Rolle am Abschluss nicht fest, entsteht wahlweise eine Zeile „Rolle fehlt“ oder gar keine."
          >
            <select
              id="rl-unassigned"
              value={formular.unassigned_policy}
              onChange={(e) => { aendereErweitert({ unassigned_policy: e.target.value === 'skip' ? 'skip' : 'emit' }) }}
            >
              <option value="emit">Zeile „Rolle fehlt“ erzeugen</option>
              <option value="skip">Zeile weglassen</option>
            </select>
          </Feld>
        </Leiste>

        <fieldset>
          <legend>Weitere Bedingungen</legend>
          {formular.conditions.map((bedingung, index) => (
            // Der Schlüssel trägt die Stelle, weil zwei Bedingungen auf dasselbe Feld zeigen dürfen.
            <p key={`bedingung-${index}`} className="aktionen">
              <select
                aria-label={`Feld der Bedingung ${index + 1}`}
                value={bedingung.field}
                onChange={(e) => {
                  aendereErweitert({
                    conditions: formular.conditions.map((eintrag, stelle) =>
                      stelle === index ? { ...eintrag, field: e.target.value } : eintrag),
                  })
                }}
              >
                {BEDINGUNGS_FELDER.map((feld) => (
                  <option key={feld.field} value={feld.field}>{feld.label}</option>
                ))}
              </select>
              <select
                aria-label={`Vergleich der Bedingung ${index + 1}`}
                value={bedingung.op}
                onChange={(e) => {
                  aendereErweitert({
                    conditions: formular.conditions.map((eintrag, stelle) =>
                      stelle === index ? { ...eintrag, op: e.target.value as BedingungsOperator } : eintrag),
                  })
                }}
              >
                <option value="eq">ist gleich</option>
                <option value="ne">ist nicht</option>
                <option value="gt">ist größer als</option>
                <option value="in">ist eine von (Komma getrennt)</option>
              </select>
              <BedingungsWert
                index={index}
                bedingung={bedingung}
                team={team}
                onAendern={(wert) => {
                  aendereErweitert({
                    conditions: formular.conditions.map((eintrag, stelle) =>
                      stelle === index ? { ...eintrag, value: wert } : eintrag),
                  })
                }}
              />
              <Knopf
                variante="still"
                onClick={() => {
                  aendereErweitert({ conditions: formular.conditions.filter((_, stelle) => stelle !== index) })
                }}
              >
                Bedingung {index + 1} entfernen
              </Knopf>
            </p>
          ))}
          <p className="aktionen">
            <Knopf
              variante="still"
              onClick={() => {
                aendereErweitert({
                  conditions: [...formular.conditions, { field: 'deal.closer_tc_id', op: 'eq', value: '' }],
                })
              }}
            >
              Bedingung hinzufügen
            </Knopf>
          </p>
        </fieldset>
      </details>

      <p className="aktionen">
        <Knopf disabled={trockenlauf.isPending || speichern.isPending} onClick={() => { absenden(false) }}>
          Regel speichern
        </Knopf>
        <Knopf variante="still" disabled={trockenlauf.isPending} onClick={pruefen}>
          Nur prüfen, nicht speichern
        </Knopf>
      </p>

      {rueckfrage && freigegeben ? (
        <div role="alertdialog" aria-label="Rückbuchungen" className="detail">
          <p className="warnung">
            Diese Regel würde bereits ausgezahlte Provisionen zurückbuchen — {geprueft.ergebnis.rueckbuchungen.length}{' '}
            {geprueft.ergebnis.rueckbuchungen.length === 1 ? 'Zeile' : 'Zeilen'}, zusammen{' '}
            <Betrag cents={geprueft.ergebnis.summe_rueckbuchungen_cents} />. Die Betroffenen sehen das auf ihrer nächsten Abrechnung.
          </p>
          <p className="knoepfe">
            <Knopf variante="warnung" disabled={speichern.isPending} onClick={() => { absenden(true) }}>Trotzdem speichern</Knopf>
            <Knopf variante="still" erstfokus onClick={() => { setzeRueckfrage(false) }}>Abbrechen</Knopf>
          </p>
        </div>
      ) : null}

      {freigegeben ? <TrockenlaufAnzeige ergebnis={geprueft.ergebnis} team={team} /> : null}

      {fehler === null ? null : <p role="alert" className="fehler">{fehler}</p>}

      {gespeichert === null ? null : (
        <>
          <p className="meldung">{MODUS_TEXT[gespeichert.modus]}</p>
          {gespeichert.neu_berechnet ? null : (
            <NeuberechnungOffen
              was="Die Regel ist gespeichert"
              laeuft={nachberechnen.isPending}
              onWiederholen={() => {
                // Derselbe Rumpf noch einmal an dieselbe Zeile: ein zweites PATCH auf die eben
                // geschriebene Regel ändert nichts und stößt nur die Neuberechnung erneut an.
                const rumpf = upsertRuleSchema.safeParse(
                  koerperAusFormular(formularAusRegel(gespeichert.regel)))
                if (!rumpf.success) {
                  setzePruefFehler(ersteMeldung(rumpf.error))
                  return
                }
                setzePruefFehler(null)
                nachberechnen.mutate({ id: gespeichert.regel.id, rumpf: rumpf.data })
              }}
            />
          )}
        </>
      )}
    </section>
  )
}

/**
 * Der Wert einer Bedingung, passend zum Feld: eine Person aus dem Team, eine Zahl, die Lead-Quelle.
 * Vorher stand hier ein Textfeld, in das man eine `tc_…`-Kennung hätte tippen müssen — die kennt
 * niemand, und so sah die Zeile aus, als gäbe es nichts auszuwählen. Nur „ist eine von“ nimmt
 * weiterhin eine Liste als Text, durch Komma getrennt.
 */
function BedingungsWert({ index, bedingung, team, onAendern }: {
  index: number
  bedingung: BedingungFormular
  team: readonly TeamZeile[]
  onAendern: (wert: string) => void
}): ReactNode {
  const beschriftung = `Wert der Bedingung ${index + 1}`
  const art = BEDINGUNGS_FELDER.find((f) => f.field === bedingung.field)?.art ?? 'text'
  if (bedingung.op !== 'in' && art === 'person') {
    return (
      <select aria-label={beschriftung} value={bedingung.value} onChange={(e) => { onAendern(e.target.value) }}>
        <option value="">bitte wählen</option>
        {team.map((zeile) => (
          <option key={zeile.tenant_contractor_id} value={zeile.tenant_contractor_id}>
            {zeile.display_name} ({zeile.short_code})
          </option>
        ))}
      </select>
    )
  }
  if (bedingung.op !== 'in' && bedingung.field === 'deal.lead_source') {
    return (
      <select aria-label={beschriftung} value={bedingung.value} onChange={(e) => { onAendern(e.target.value) }}>
        <option value="">bitte wählen</option>
        <option value="extern">extern — selbst geholt</option>
        <option value="intern">intern — von der Firma gestellt</option>
      </select>
    )
  }
  return (
    <input
      aria-label={beschriftung}
      type="text"
      inputMode={art === 'zahl' ? 'numeric' : undefined}
      placeholder={bedingung.op === 'in' ? 'Werte, durch Komma getrennt' : art === 'zahl' ? 'Zahl' : ''}
      value={bedingung.value}
      onChange={(e) => { onAendern(e.target.value) }}
    />
  )
}

/**
 * Was das Speichern an den offenen Provisionen ändern würde — drei Blöcke, drei Summen, alle vom
 * Server. Hier wird nichts addiert: die Summen stehen in der Antwort, damit auf dem Bildschirm
 * dieselbe Zahl steht, mit der der Server gleich rechnet.
 */
function TrockenlaufAnzeige({ ergebnis, team }: {
  ergebnis: ProbeErgebnis
  team: readonly TeamZeile[]
}): ReactNode {
  const bloecke: ReadonlyArray<{ titel: string; zeilen: ProbeZeile[]; summe: number }> = [
    { titel: 'Neue Zeilen', zeilen: ergebnis.neu, summe: ergebnis.summe_neu_cents },
    { titel: 'Entfallende Zeilen', zeilen: ergebnis.entfallen, summe: ergebnis.summe_entfallen_cents },
    { titel: 'Rückbuchungen', zeilen: ergebnis.rueckbuchungen, summe: ergebnis.summe_rueckbuchungen_cents },
  ]

  return (
    <section className="detail" aria-label="Ergebnis des Trockenlaufs">
      <p>Diese Änderung wirkt auf die offenen Provisionen wie folgt:</p>
      {bloecke.map((block) => (
        <div key={block.titel}>
          <h3>{`${block.titel} (${block.zeilen.length})`}</h3>
          {block.zeilen.length === 0 ? null : (
            <Tabelle
              beschriftung={block.titel}
              kopf={
                <tr>
                  <th scope="col">Empfänger</th>
                  <th scope="col">Rolle</th>
                  <th scope="col" className="zahl">Betrag</th>
                </tr>
              }
            >
              {block.zeilen.map((zeile, index) => (
                <tr key={zeile.commission_id ?? `${zeile.delta_id ?? 'neu'}-${index}`}>
                  <td>{empfaengerName(zeile.recipient_tc_id, team)}</td>
                  <td>{zeile.role_label}</td>
                  <td className="zahl"><Betrag cents={zeile.amount_cents} /></td>
                </tr>
              ))}
            </Tabelle>
          )}
          <p className="notiz">Summe: <Betrag cents={block.summe} /></p>
        </div>
      ))}
    </section>
  )
}

/**
 * Produkte und ihre Suchbegriffe (spec §1.4). Der Kasten „Invoice-Name testen“ fragt den Server, in
 * welcher Reihenfolge die Begriffe wirken und welcher trifft — die Reihenfolge ist eine Regel des
 * Servers (längster Begriff zuerst), und sie wird hier nicht nachgebaut, sondern angezeigt.
 */
function ProduktBereich({ tenantId, produkte, laedt, fehler: ladeFehler, onNeuLaden }: {
  tenantId: string
  produkte: readonly ProduktMitBegriffen[]
  laedt: boolean
  fehler: unknown
  onNeuLaden: () => void
}): ReactNode {
  const tid = encodeURIComponent(tenantId)
  const queryClient = useQueryClient()
  const [gewaehlt, setzeGewaehlt] = useState<string | null>(null)
  const [code, setzeCode] = useState('')
  const [bezeichnung, setzeBezeichnung] = useState('')
  const [kuerzelManuell, setzeKuerzelManuell] = useState(false)
  const [begriffe, setzeBegriffe] = useState('')
  const [testName, setzeTestName] = useState('')
  const [pruefFehler, setzePruefFehler] = useState<string | null>(null)
  const [testPruefFehler, setzeTestPruefFehler] = useState<string | null>(null)

  const produkt = produkte.find((eintrag) => eintrag.id === gewaehlt) ?? null
  useInsBild(produkt === null ? null : 'produkt-editor')

  // Die drei Eingaben gehören zum gewählten Produkt; wechselt es, tragen sie sonst noch die Werte
  // des vorigen — und „Suchbegriffe ersetzen“ würde die falsche Liste überschreiben.
  useEffect(() => {
    setzeCode(produkt?.code ?? '')
    setzeBezeichnung(produkt?.name ?? '')
    setzeKuerzelManuell(false)
    setzeBegriffe((produkt?.terms ?? []).map((term) => term.term).join('\n'))
  }, [produkt])

  const neuLaden = (): void => {
    for (const schl of ['produkte', 'inbox', 'provisionen', 'dashboard']) {
      void queryClient.invalidateQueries({ queryKey: [schl, tenantId] })
    }
  }

  // Das Ziel steht im Auftrag, nicht im Abschluss: der zweite Anlauf nach einer ausgefallenen
  // Neuberechnung muss auf dieselbe Zeile zeigen wie der erste. Ein zweites POST auf /produkte wäre
  // kein Wiederholen, sondern ein zweites Produkt — der Server antwortete mit `code_vergeben`.
  const produktSpeichern = useMutation({
    mutationFn: (ziel: { id: string | null; rumpf: unknown }) => ziel.id === null
      ? api.post<ProduktAntwort>(`/api/t/${tid}/produkte`, ziel.rumpf)
      : api.patch<ProduktAntwort>(`/api/t/${tid}/produkte/${encodeURIComponent(ziel.id)}`, ziel.rumpf),
    onSuccess: neuLaden,
  })
  const begriffeSpeichern = useMutation({
    mutationFn: (ziel: { id: string; rumpf: unknown }) =>
      api.post<{ terms: BegriffZeile[]; neu_berechnet: boolean }>(
        `/api/t/${tid}/produkte/${encodeURIComponent(ziel.id)}/terms`, ziel.rumpf),
    onSuccess: neuLaden,
  })
  const test = useMutation({
    mutationFn: (rumpf: unknown) => api.post<MatchTestErgebnis>(`/api/t/${tid}/produkte/match-test`, rumpf),
  })

  // Zwei Meldungsstellen: was beim Speichern von Produkt oder Suchbegriffen scheitert, steht am
  // Editor — der aufgeklappt in der Tabellenzeile stehen kann —, was beim Testen scheitert, am Test.
  const editorFehler = pruefFehler
    ?? (produktSpeichern.isError ? fehlermeldung(produktSpeichern.error) : null)
    ?? (begriffeSpeichern.isError ? fehlermeldung(begriffeSpeichern.error) : null)
  const testFehler = testPruefFehler ?? (test.isError ? fehlermeldung(test.error) : null)

  // Der Editor steht unter der Zeile, die man bearbeitet; „Neues Produkt" bleibt unter der Tabelle.
  const produktEditor = (
    <div className="produkt-editor" id="produkt-editor">
        <h3>{produkt === null ? 'Neues Produkt' : `Produkt ${produkt.code}`}</h3>
        <Leiste beschriftung="Produkt bearbeiten">
          <Feld label="Produktname" htmlFor="pr-name">
            <input id="pr-name" type="text" value={bezeichnung}
              onChange={(e) => {
                setzeBezeichnung(e.target.value)
                // Bei einem neuen Produkt folgt das Kürzel dem ersten Wort des Namens, bis jemand es
                // selbst schreibt: „VIP Podcast“ → VIP, „Founder Program“ → FOUNDER.
                if (produkt === null && !kuerzelManuell) setzeCode(kuerzelAus(e.target.value))
              }} />
          </Feld>
          <Feld label="Kürzel" htmlFor="pr-code" info="Kurz und eindeutig; steht auf Belegen und im Export.">
            <input id="pr-code" type="text" value={code}
              onChange={(e) => { setzeKuerzelManuell(true); setzeCode(e.target.value) }} />
          </Feld>
        </Leiste>
        <p className="aktionen">
          <Knopf
            disabled={produktSpeichern.isPending}
            onClick={() => {
              const geprueft = upsertProductSchema.safeParse({ code, name: bezeichnung })
              if (!geprueft.success) {
                setzePruefFehler(ersteMeldung(geprueft.error))
                return
              }
              setzePruefFehler(null)
              produktSpeichern.mutate({ id: produkt?.id ?? null, rumpf: geprueft.data })
            }}
          >
            Produkt speichern
          </Knopf>
          <Knopf variante="still" onClick={() => { setzeGewaehlt(null) }}>Neues Produkt</Knopf>
        </p>
        {produktSpeichern.data === undefined || produktSpeichern.data.neu_berechnet ? null : (
          <NeuberechnungOffen
            was="Das Produkt ist gespeichert"
            laeuft={produktSpeichern.isPending}
            onWiederholen={() => {
              const auftrag = produktSpeichern.variables
              if (auftrag === undefined || produktSpeichern.data === undefined) return
              // Der zweite Anlauf ist immer ein PATCH auf die eben geschriebene Zeile — auch dann, wenn
              // der erste sie angelegt hat. Dieselben Werte noch einmal: es ändert nichts und rechnet nach.
              produktSpeichern.mutate({ id: produktSpeichern.data.produkt.id, rumpf: auftrag.rumpf })
            }}
          />
        )}

        {produkt === null ? null : (
          <>
            <Feld
              label={`Auch bekannt als — ein Name je Zeile (${produkt.code})`}
              htmlFor="pr-terms"
              hinweis="Groß/Klein ist egal. Ein Name bis drei Zeichen (etwa „FP“) muss als eigenes Wort vorkommen, ein längerer irgendwo im Text; der längste Treffer gewinnt. Die Liste wird vollständig ersetzt — ein entfernter Name erkennt danach nichts mehr."
            >
              <textarea
                id="pr-terms"
                rows={5}
                value={begriffe}
                onChange={(e) => { setzeBegriffe(e.target.value) }}
              />
            </Feld>
            <p className="aktionen">
              <Knopf
                disabled={begriffeSpeichern.isPending}
                onClick={() => {
                  const terms = begriffe.split('\n').map((zeile) => zeile.trim()).filter((zeile) => zeile !== '')
                  const geprueft = replaceTermsSchema.safeParse({ terms })
                  if (!geprueft.success) {
                    setzePruefFehler(ersteMeldung(geprueft.error))
                    return
                  }
                  setzePruefFehler(null)
                  begriffeSpeichern.mutate({ id: produkt.id, rumpf: geprueft.data })
                }}
              >
                Suchbegriffe ersetzen
              </Knopf>
            </p>
            {begriffeSpeichern.data === undefined || begriffeSpeichern.data.neu_berechnet ? null : (
              <NeuberechnungOffen
                was="Die Suchbegriffe sind gespeichert"
                laeuft={begriffeSpeichern.isPending}
                onWiederholen={() => {
                  // Dieselbe Liste noch einmal: POST ersetzt sie vollständig, ist also wiederholbar.
                  const auftrag = begriffeSpeichern.variables
                  if (auftrag !== undefined) begriffeSpeichern.mutate(auftrag)
                }}
              />
            )}
          </>
        )}
      {editorFehler === null ? null : <p role="alert" className="fehler">{editorFehler}</p>}
    </div>
  )

  return (
    <section className="detail" aria-label="Produkte und Suchbegriffe">
      <h2>Produkte</h2>
      <p className="hinweis">
        Jedes Produkt braucht die Namen, unter denen es in Rechnungen und Verträgen vorkommt — „Founder Program“, „FP“,
        „Founders Programm“. Daran erkennt das System, welches Produkt eine Zahlung oder ein Vertrag meint.
      </p>

      <Tabelle
        beschriftung="Produkte dieses Auftraggebers"
        kopf={
          <tr>
            <th scope="col">Kürzel</th>
            <th scope="col">Name</th>
            <th scope="col" className="zahl">Auch bekannt als</th>
            <th scope="col">Aktiv</th>
            <th scope="col"><span className="nur-vorlesen">Bearbeiten</span></th>
          </tr>
        }
      >
        {laedt ? (
          <TabellenZustand art="laedt" spalten={SPALTEN_PRODUKTE} beschriftung="Die Produkte werden geladen." />
        ) : null}
        {ladeFehler === null ? null : (
          <TabellenZustand
            art="fehler"
            spalten={SPALTEN_PRODUKTE}
            fehler={ladeFehler}
            onWiederholen={onNeuLaden}
          />
        )}
        {!laedt && ladeFehler === null && produkte.length === 0 ? (
          <TabellenZustand
            art="leer"
            spalten={SPALTEN_PRODUKTE}
            titel="Noch kein Produkt angelegt"
            hinweis="Ein Produkt bündelt die Rechnungsnamen, unter denen es abgerechnet wird. Ohne Produkt bleibt jede Zahlung in der Prüfliste."
          />
        ) : null}
        {produkte.map((eintrag) => (
          <Fragment key={eintrag.id}>
            <tr aria-selected={eintrag.id === produkt?.id ? true : undefined}>
              <th scope="row" className="mono">{eintrag.code}</th>
              <td>{eintrag.name}</td>
              <td className="zahl">{eintrag.terms.length}</td>
              <td>{eintrag.active === 1 ? 'ja' : 'nein'}</td>
              <td>
                <Knopf variante="still" onClick={() => { setzeGewaehlt(eintrag.id) }}>Bearbeiten</Knopf>
              </td>
            </tr>
            {eintrag.id === produkt?.id ? (
              <tr className="zeile-detail">
                <td colSpan={SPALTEN_PRODUKTE}>
                  <div className="zeile-detail-inhalt">{produktEditor}</div>
                </td>
              </tr>
            ) : null}
          </Fragment>
        ))}
      </Tabelle>

      {produkt === null ? produktEditor : null}

      <Feld
        label="Invoice-Name testen"
        htmlFor="pr-test"
        hinweis="Der Test schreibt nichts; er zeigt nur, welches Produkt dieser Name heute treffen würde."
      >
        <input id="pr-test" type="text" value={testName}
          onChange={(e) => { setzeTestName(e.target.value) }} />
      </Feld>
      <p className="aktionen">
        <Knopf
          disabled={test.isPending}
          onClick={() => {
            const geprueft = matchTestSchema.safeParse({ invoice_name: testName })
            if (!geprueft.success) {
              setzeTestPruefFehler(ersteMeldung(geprueft.error))
              return
            }
            setzeTestPruefFehler(null)
            test.mutate(geprueft.data)
          }}
        >
          Testen
        </Knopf>
      </p>

      {test.data === undefined ? null : (
        <>
          <p className="meldung">
            {test.data.product === null
              ? 'Kein Produkt erkannt'
              : `Erkanntes Produkt: ${test.data.product.code} — ${test.data.product.name}`}
          </p>
          <Tabelle
            beschriftung="Wirksame Reihenfolge der Suchbegriffe"
            kopf={
              <tr>
                <th scope="col">Reihenfolge</th>
                <th scope="col">Suchbegriff</th>
                <th scope="col">Produkt</th>
                <th scope="col">Treffer</th>
              </tr>
            }
          >
            {test.data.effective_terms.map((term, index) => (
              <tr key={term.id}>
                <th scope="row" className="mono">{index + 1}</th>
                <td>{term.term}</td>
                <td className="mono">{term.product_code}</td>
                <td>{term.matches ? 'trifft zu' : '—'}</td>
              </tr>
            ))}
          </Tabelle>
        </>
      )}

      {testFehler === null ? null : <p role="alert" className="fehler">{testFehler}</p>}
    </section>
  )
}
